import * as React from "react";
import { useNavigate } from "react-router-dom";
import * as Effect from "effect/Effect";
import { pipe } from "effect/Function";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarClock, Link2, Pencil, Plus, ReceiptText, Repeat, Trash2, XCircle } from "lucide-react";

import { ApiClient } from "@/api/client";
import { MainLayer } from "@/effects/layers";
import { runEffect } from "@/effects/runtime";
import {
  CreateRecurringExpenseResponseSchema,
  PreviewBackfillResponseSchema,
  RecurringTransactionsResponseSchema,
  UpdateRecurringExpenseResponseSchema,
  type BackfillPreviewRow,
  type RecurringExpense,
  type RecurringTransaction,
  type RepeatInterval,
} from "@/api/schemas/recurring.schema";
import { useRecurringExpenses, RECURRING_EXPENSES_QUERY_KEY } from "@/hooks/queries/useRecurringExpenses";
import { useGroupCategories } from "@/hooks/queries/useCategories";
import { useGroupUsers } from "@/hooks/queries/useGroups";
import { useActiveGroup } from "@/stores/group.store";

import { ResponsiveContainer } from "@/components/layout/responsive-grid";
import { LoadingState } from "@/components/ui/loading-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const todayDateOnly = () => new Date().toISOString().slice(0, 10);

const intervalLabel = (interval: RepeatInterval, count: number): string => {
  if (count <= 1) {
    return { weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" }[interval];
  }
  const unit = { weekly: "weeks", monthly: "months", yearly: "years" }[interval];
  return `Every ${count} ${unit}`;
};

interface RuleFormState {
  description: string;
  amount: string;
  category_id: string;
  paid_by: string;
  repeat_interval: RepeatInterval;
  interval_count: string;
  start_date: string;
  hasEndDate: boolean;
  end_date: string;
  effective_date: string;
}

const emptyForm = (): RuleFormState => ({
  description: "",
  amount: "",
  category_id: "",
  paid_by: "",
  repeat_interval: "monthly",
  interval_count: "1",
  start_date: todayDateOnly(),
  hasEndDate: false,
  end_date: "",
  effective_date: todayDateOnly(),
});

const formFromRule = (rule: RecurringExpense): RuleFormState => ({
  description: rule.description,
  amount: String(rule.amount),
  category_id: rule.category?.id ?? "",
  paid_by: rule.paid_by?.id ?? "",
  repeat_interval: rule.repeat_interval,
  interval_count: String(rule.interval_count),
  start_date: rule.start_date,
  hasEndDate: rule.end_date !== null,
  end_date: rule.end_date ?? "",
  effective_date: todayDateOnly(),
});

export const RecurringExpensesPage: React.FC = () => {
  const { activeGroupId } = useActiveGroup();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const goToTransaction = (transactionId: string) => {
    navigate(`/expenses?highlight=${encodeURIComponent(transactionId)}`);
  };

  const { data: rules = [], isLoading, error } = useRecurringExpenses(activeGroupId || "");
  const { data: categories = [] } = useGroupCategories(activeGroupId || "");
  const { data: members = [] } = useGroupUsers(activeGroupId || "");

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingRule, setEditingRule] = React.useState<RecurringExpense | null>(null);
  const [form, setForm] = React.useState<RuleFormState>(emptyForm());
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [cancelTarget, setCancelTarget] = React.useState<RecurringExpense | null>(null);
  const [removePastFrom, setRemovePastFrom] = React.useState<string>("");
  const [isCancelling, setIsCancelling] = React.useState(false);
  const [purgeTarget, setPurgeTarget] = React.useState<RecurringExpense | null>(null);
  const [isPurging, setIsPurging] = React.useState(false);

  // Per-rule transaction list dialog (generated + linked).
  const [transactionsTarget, setTransactionsTarget] = React.useState<RecurringExpense | null>(null);
  const [ruleTransactions, setRuleTransactions] = React.useState<RecurringTransaction[] | null>(null);

  // Backfill step: when creating a rule with past occurrences — or updating
  // one in a way that newly materializes slots (reactivation, end-date
  // extension) — existing expenses that look like the same charges are
  // offered for linking before anything is materialized. choices:
  // occurrence_date -> 'create' | 'skip' | candidate expense id.
  const [backfillRows, setBackfillRows] = React.useState<BackfillPreviewRow[] | null>(null);
  const [backfillChoices, setBackfillChoices] = React.useState<Record<string, string>>({});
  const [pendingCreateBody, setPendingCreateBody] = React.useState<Record<string, unknown> | null>(null);
  // Set instead of pendingCreateBody when the pending action is an update.
  const [pendingUpdate, setPendingUpdate] = React.useState<{ ruleId: string; body: Record<string, unknown> } | null>(null);

  const setField = <K extends keyof RuleFormState>(key: K, value: RuleFormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [RECURRING_EXPENSES_QUERY_KEY, activeGroupId] });
  };

  const openCreate = () => {
    setEditingRule(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (rule: RecurringExpense) => {
    setEditingRule(rule);
    setForm(formFromRule(rule));
    setDialogOpen(true);
  };

  const validateForm = (): string | null => {
    if (!form.description.trim()) return "Description is required";
    const amount = Number(form.amount.replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) return "Amount must be a positive number";
    if (!form.category_id) return "Category is required";
    if (!form.paid_by) return "Paid by is required";
    if (!form.start_date) return "Start date is required";
    if (form.hasEndDate && !form.end_date) return "End date is required (or untick 'ends on')";
    if (form.hasEndDate && form.end_date < form.start_date) return "End date must be after the start date";
    const count = Number(form.interval_count);
    if (!Number.isInteger(count) || count < 1 || count > 52) return "Repeat every must be between 1 and 52";
    return null;
  };

  const submit = async () => {
    if (!activeGroupId) return;
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const amount = Number(form.amount.replace(",", "."));
    setIsSubmitting(true);
    try {
      if (editingRule) {
        const body = {
          effective_date: form.effective_date,
          description: form.description.trim(),
          amount,
          category_id: form.category_id,
          paid_by: form.paid_by,
          repeat_interval: form.repeat_interval,
          interval_count: Number(form.interval_count),
          end_date: form.hasEndDate ? form.end_date : null,
          ...(editingRule.is_active ? {} : { is_active: true }),
        };

        // If this update newly materializes slots (reactivation gap, extended
        // end date), offer to link existing expenses first — same flow as
        // creation, so reactivating over CSV-imported charges can't duplicate.
        const previewProgram = pipe(
          Effect.gen(function* () {
            const apiClient = yield* ApiClient;
            return yield* apiClient.post(
              `/groups/${activeGroupId}/recurring-expenses/${editingRule.id}/preview`,
              body,
              PreviewBackfillResponseSchema
            );
          }),
          Effect.provide(MainLayer)
        );
        const preview = await runEffect(previewProgram);
        const rowsWithCandidates = preview.occurrences.filter((row) => row.candidates.length > 0);

        if (rowsWithCandidates.length > 0) {
          const defaults: Record<string, string> = {};
          for (const row of preview.occurrences) {
            defaults[row.occurrence_date] = row.candidates.length > 0 ? row.candidates[0].id : "create";
          }
          setBackfillChoices(defaults);
          setBackfillRows(preview.occurrences);
          setPendingUpdate({ ruleId: editingRule.id, body });
          setDialogOpen(false);
          return;
        }

        await performUpdate(editingRule.id, body, [], []);
      } else {
        const body = {
          description: form.description.trim(),
          amount,
          currency_code: "EUR",
          category_id: form.category_id,
          paid_by: form.paid_by,
          repeat_interval: form.repeat_interval,
          interval_count: Number(form.interval_count),
          start_date: form.start_date,
          end_date: form.hasEndDate ? form.end_date : null,
        };

        // Before creating: check whether past occurrences match expenses that
        // already exist (manual entries, CSV imports). If so, let the user
        // link them instead of generating duplicates.
        const previewProgram = pipe(
          Effect.gen(function* () {
            const apiClient = yield* ApiClient;
            return yield* apiClient.post(
              `/groups/${activeGroupId}/recurring-expenses/preview`,
              {
                amount: body.amount,
                repeat_interval: body.repeat_interval,
                interval_count: body.interval_count,
                start_date: body.start_date,
                end_date: body.end_date,
              },
              PreviewBackfillResponseSchema
            );
          }),
          Effect.provide(MainLayer)
        );
        const preview = await runEffect(previewProgram);
        const rowsWithCandidates = preview.occurrences.filter((row) => row.candidates.length > 0);

        if (rowsWithCandidates.length > 0) {
          // Default every matched occurrence to linking its nearest candidate.
          const defaults: Record<string, string> = {};
          for (const row of preview.occurrences) {
            defaults[row.occurrence_date] = row.candidates.length > 0 ? row.candidates[0].id : "create";
          }
          setBackfillChoices(defaults);
          setBackfillRows(preview.occurrences);
          setPendingCreateBody(body);
          setDialogOpen(false);
          return;
        }

        await performCreate(body, [], []);
      }
    } catch {
      toast.error(editingRule ? "Failed to update recurring expense" : "Failed to create recurring expense");
    } finally {
      setIsSubmitting(false);
    }
  };

  const performUpdate = async (
    ruleId: string,
    body: Record<string, unknown>,
    adoptions: Array<{ occurrence_date: string; expense_id: string }>,
    skipOccurrences: string[]
  ) => {
    if (!activeGroupId) return;
    const program = pipe(
      Effect.gen(function* () {
        const apiClient = yield* ApiClient;
        return yield* apiClient.put(
          `/groups/${activeGroupId}/recurring-expenses/${ruleId}`,
          {
            ...body,
            ...(adoptions.length > 0 ? { adoptions } : {}),
            ...(skipOccurrences.length > 0 ? { skip_occurrences: skipOccurrences } : {}),
          },
          UpdateRecurringExpenseResponseSchema
        );
      }),
      Effect.provide(MainLayer)
    );
    const result = await runEffect(program);
    const parts = [
      ...(result.deleted_occurrences > 0 ? [`${result.deleted_occurrences} rewritten`] : []),
      ...(result.created_occurrences > 0 ? [`${result.created_occurrences} generated`] : []),
      ...(adoptions.length > 0 ? [`${adoptions.length} linked to existing`] : []),
    ];
    toast.success(parts.length > 0
      ? `Updated from ${result.effective_date} — ${parts.join(", ")}`
      : `Updated from ${result.effective_date}`);
    setDialogOpen(false);
    setBackfillRows(null);
    setPendingUpdate(null);
    invalidate();
    toast.info("Generated transactions appear in the list after the next sync");
  };

  const performCreate = async (
    body: Record<string, unknown>,
    adoptions: Array<{ occurrence_date: string; expense_id: string }>,
    skipOccurrences: string[]
  ) => {
    if (!activeGroupId) return;
    const program = pipe(
      Effect.gen(function* () {
        const apiClient = yield* ApiClient;
        return yield* apiClient.post(
          `/groups/${activeGroupId}/recurring-expenses`,
          {
            ...body,
            ...(adoptions.length > 0 ? { adoptions } : {}),
            ...(skipOccurrences.length > 0 ? { skip_occurrences: skipOccurrences } : {}),
          },
          CreateRecurringExpenseResponseSchema
        );
      }),
      Effect.provide(MainLayer)
    );
    const result = await runEffect(program);
    const parts = [
      ...(result.materialized_count > 0 ? [`${result.materialized_count} generated`] : []),
      ...((result.adopted_count ?? 0) > 0 ? [`${result.adopted_count} linked to existing`] : []),
    ];
    toast.success(parts.length > 0
      ? `Recurring expense created — ${parts.join(", ")}`
      : "Recurring expense created");
    setDialogOpen(false);
    setBackfillRows(null);
    setPendingCreateBody(null);
    invalidate();
    toast.info("Generated transactions appear in the list after the next sync");
  };

  const confirmBackfill = async () => {
    if ((!pendingCreateBody && !pendingUpdate) || !backfillRows) return;
    setIsSubmitting(true);
    try {
      const adoptions: Array<{ occurrence_date: string; expense_id: string }> = [];
      const skips: string[] = [];
      for (const row of backfillRows) {
        const choice = backfillChoices[row.occurrence_date] ?? "create";
        if (choice === "skip") {
          skips.push(row.occurrence_date);
        } else if (choice !== "create") {
          adoptions.push({ occurrence_date: row.occurrence_date, expense_id: choice });
        }
      }
      if (pendingUpdate) {
        await performUpdate(pendingUpdate.ruleId, pendingUpdate.body, adoptions, skips);
      } else if (pendingCreateBody) {
        await performCreate(pendingCreateBody, adoptions, skips);
      }
    } catch {
      toast.error(pendingUpdate ? "Failed to update recurring expense" : "Failed to create recurring expense");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmCancel = async () => {
    if (!activeGroupId || !cancelTarget) return;
    setIsCancelling(true);
    try {
      const query = removePastFrom ? `?removeFrom=${removePastFrom}` : "";
      const cancelProgram = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          return yield* apiClient.delete(
            `/groups/${activeGroupId}/recurring-expenses/${cancelTarget.id}${query}`
          );
        }),
        Effect.provide(MainLayer)
      );
      await runEffect(cancelProgram);
      toast.success(
        removePastFrom
          ? `Cancelled — transactions from ${removePastFrom} removed`
          : "Cancelled — past transactions kept"
      );
      setCancelTarget(null);
      setRemovePastFrom("");
      invalidate();
    } catch {
      toast.error("Failed to cancel recurring expense");
    } finally {
      setIsCancelling(false);
    }
  };

  const openTransactions = async (rule: RecurringExpense) => {
    if (!activeGroupId) return;
    setTransactionsTarget(rule);
    setRuleTransactions(null);
    try {
      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          return yield* apiClient.get(
            `/groups/${activeGroupId}/recurring-expenses/${rule.id}/transactions`,
            RecurringTransactionsResponseSchema
          );
        }),
        Effect.provide(MainLayer)
      );
      const result = await runEffect(program);
      setRuleTransactions(result.transactions);
    } catch {
      toast.error("Failed to load transactions");
      setRuleTransactions([]);
    }
  };

  const confirmPurge = async () => {
    if (!activeGroupId || !purgeTarget) return;
    setIsPurging(true);
    try {
      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          return yield* apiClient.delete(
            `/groups/${activeGroupId}/recurring-expenses/${purgeTarget.id}?purge=true`
          );
        }),
        Effect.provide(MainLayer)
      );
      await runEffect(program);
      toast.success("Recurring expense removed — all transactions kept");
      setPurgeTarget(null);
      invalidate();
    } catch {
      toast.error("Failed to remove recurring expense");
    } finally {
      setIsPurging(false);
    }
  };

  if (!activeGroupId) {
    return (
      <ResponsiveContainer size="xl">
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold text-muted-foreground mb-2">No active group</h2>
          <p className="text-muted-foreground">Select a group to manage recurring expenses</p>
        </div>
      </ResponsiveContainer>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading recurring expenses..." />;
  }

  if (error) {
    return (
      <ResponsiveContainer size="xl">
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold text-destructive mb-2">Failed to load recurring expenses</h2>
          <p className="text-muted-foreground">Please try refreshing the page</p>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer size="xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Recurring</h1>
            <p className="text-muted-foreground">
              Subscriptions and other repeating expenses, generated automatically
            </p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New recurring expense
          </Button>
        </div>

        {rules.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Repeat className="h-8 w-8 mx-auto mb-3 opacity-50" />
              <p>No recurring expenses yet.</p>
              <p className="text-sm">Add your subscriptions and they will be booked automatically.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {rules.map((rule) => (
              <Card key={rule.id} className={rule.is_active ? "" : "opacity-60"}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {rule.category?.emoji && <span>{rule.category.emoji}</span>}
                      {rule.description}
                    </CardTitle>
                    <Badge variant={rule.is_active ? "default" : "secondary"}>
                      {rule.is_active ? intervalLabel(rule.repeat_interval, rule.interval_count) : "Cancelled"}
                    </Badge>
                  </div>
                  <CardDescription>
                    {rule.amount.toFixed(2)} {rule.currency_code}
                    {rule.paid_by?.name ? ` · paid by ${rule.paid_by.name}` : ""}
                    {rule.category?.name ? ` · ${rule.category.name}` : ""}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm text-muted-foreground space-y-1">
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-4 w-4" />
                      <span>
                        {rule.start_date}
                        {" → "}
                        {rule.end_date ?? "until cancelled"}
                      </span>
                    </div>
                    {rule.is_active && rule.next_due_date && (
                      <div>Next: {rule.next_due_date}</div>
                    )}
                    <div>{rule.materialized_count} transaction(s) generated</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(rule)}>
                      <Pencil className="h-3.5 w-3.5 mr-1.5" />
                      {rule.is_active ? "Edit" : "Edit & reactivate"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openTransactions(rule)}>
                      <ReceiptText className="h-3.5 w-3.5 mr-1.5" />
                      Transactions
                    </Button>
                    {rule.is_active && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setCancelTarget(rule);
                          setRemovePastFrom("");
                        }}
                      >
                        <XCircle className="h-3.5 w-3.5 mr-1.5" />
                        Cancel
                      </Button>
                    )}
                    {!rule.is_active && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive"
                        onClick={() => setPurgeTarget(rule)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        Remove
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? "Edit recurring expense" : "New recurring expense"}</DialogTitle>
            <DialogDescription>
              {editingRule
                ? "Changes apply from the effective date: transactions on or after it are rewritten, earlier ones are kept as they were."
                : "The expense is booked automatically on schedule and split like a normal expense."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {editingRule && (
              <div className="space-y-1.5">
                <Label htmlFor="effective_date">Apply changes from</Label>
                <Input
                  id="effective_date"
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => setField("effective_date", e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Pick a past date to correct history (e.g. a price change you missed) or a future date to schedule the change.
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Netflix, rent, gym…"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="amount">Amount (EUR)</Label>
                <Input
                  id="amount"
                  inputMode="decimal"
                  placeholder="9.99"
                  value={form.amount}
                  onChange={(e) => setField("amount", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category_id} onValueChange={(value) => setField("category_id", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.emoji ? `${category.emoji} ` : ""}{category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Paid by</Label>
                <Select value={form.paid_by} onValueChange={(value) => setField("paid_by", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Who pays?" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name || member.email || member.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Repeats</Label>
                <div className="flex gap-2">
                  <Input
                    className="w-16"
                    inputMode="numeric"
                    value={form.interval_count}
                    onChange={(e) => setField("interval_count", e.target.value)}
                  />
                  <Select
                    value={form.repeat_interval}
                    onValueChange={(value) => setField("repeat_interval", value as RepeatInterval)}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">week(s)</SelectItem>
                      <SelectItem value="monthly">month(s)</SelectItem>
                      <SelectItem value="yearly">year(s)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="start_date">First occurrence</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={form.start_date}
                  disabled={!!editingRule}
                  onChange={(e) => setField("start_date", e.target.value)}
                />
                {!editingRule && form.start_date && form.start_date < todayDateOnly() && (
                  <p className="text-xs text-muted-foreground">
                    Past occurrences since this date will be generated immediately.
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 pt-0.5">
                  <Checkbox
                    id="hasEndDate"
                    checked={form.hasEndDate}
                    onCheckedChange={(checked) => setField("hasEndDate", checked === true)}
                  />
                  <Label htmlFor="hasEndDate">Ends on</Label>
                </div>
                <Input
                  type="date"
                  value={form.end_date}
                  disabled={!form.hasEndDate}
                  onChange={(e) => setField("end_date", e.target.value)}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={isSubmitting}>
              {isSubmitting ? "Saving…" : editingRule ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backfill step: link past occurrences to already-recorded expenses */}
      <Dialog
        open={!!backfillRows}
        onOpenChange={(open) => {
          if (!open) {
            setBackfillRows(null);
            setPendingCreateBody(null);
            setPendingUpdate(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Some of these may already exist</DialogTitle>
            <DialogDescription>
              Existing transactions match the amount near these dates — they may be the same charge
              under a different name. Link them to avoid duplicates, or create new ones.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {backfillRows?.map((row) => (
              <div key={row.occurrence_date} className="flex items-center gap-3">
                <span className="w-28 shrink-0 text-sm tabular-nums">{row.occurrence_date}</span>
                <Select
                  value={backfillChoices[row.occurrence_date] ?? "create"}
                  onValueChange={(value) =>
                    setBackfillChoices((current) => ({ ...current, [row.occurrence_date]: value }))
                  }
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {row.candidates.map((candidate) => (
                      <SelectItem key={candidate.id} value={candidate.id}>
                        Link: {candidate.description || "(no description)"} — {candidate.date}
                        {candidate.category_name ? ` (${candidate.category_name})` : ""}
                      </SelectItem>
                    ))}
                    <SelectItem value="create">Create new transaction</SelectItem>
                    <SelectItem value="skip">Skip this date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Linked transactions are renamed to the subscription and use its category and split from now on.
          </p>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBackfillRows(null);
                setPendingCreateBody(null);
                setPendingUpdate(null);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={confirmBackfill} disabled={isSubmitting}>
              {isSubmitting ? "Saving…" : pendingUpdate ? "Confirm & save" : "Confirm & create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transactions dialog: everything this rule owns (generated + linked) */}
      <Dialog
        open={!!transactionsTarget}
        onOpenChange={(open) => {
          if (!open) {
            setTransactionsTarget(null);
            setRuleTransactions(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Transactions for "{transactionsTarget?.description}"</DialogTitle>
            <DialogDescription>
              Every transaction this subscription generated or is linked to.
            </DialogDescription>
          </DialogHeader>

          {ruleTransactions === null ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
          ) : ruleTransactions.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No transactions yet.
            </div>
          ) : (
            <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-1">
              {ruleTransactions.map((transaction) => (
                <button
                  type="button"
                  key={transaction.id}
                  onClick={() => goToTransaction(transaction.id)}
                  className="flex w-full items-center gap-3 rounded-md border p-2.5 text-left transition-colors hover:bg-accent"
                  title="View in transactions"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">
                        {transaction.category?.emoji ? `${transaction.category.emoji} ` : ""}
                        {transaction.description || "(no description)"}
                      </span>
                      <Badge variant={transaction.adopted ? "outline" : "secondary"} className="gap-1 shrink-0">
                        {transaction.adopted
                          ? <><Link2 className="h-3 w-3" />Linked</>
                          : <><Repeat className="h-3 w-3" />Generated</>}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {transaction.date}
                      {transaction.paid_by.name ? ` · ${transaction.paid_by.name}` : ""}
                    </div>
                  </div>
                  <div className="text-sm font-semibold tabular-nums shrink-0">
                    {transaction.amount.toFixed(2)} {transaction.currency_code}
                  </div>
                </button>
              ))}
            </div>
          )}

          {ruleTransactions && ruleTransactions.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {ruleTransactions.filter((t) => !t.adopted).length} generated,{" "}
              {ruleTransactions.filter((t) => t.adopted).length} linked.
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Purge dialog: permanently remove a cancelled rule's setup */}
      <Dialog open={!!purgeTarget} onOpenChange={(open) => !open && setPurgeTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove "{purgeTarget?.description}"?</DialogTitle>
            <DialogDescription>
              This removes the recurring setup permanently. All transactions are kept — they
              become ordinary transactions, no longer tied to a subscription.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeTarget(null)} disabled={isPurging}>
              Keep it
            </Button>
            <Button variant="destructive" onClick={confirmPurge} disabled={isPurging}>
              {isPurging ? "Removing…" : "Remove setup"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel "{cancelTarget?.description}"?</DialogTitle>
            <DialogDescription>
              No further transactions will be generated. Already generated transactions are kept unless you remove them below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="removeFrom">Also remove generated transactions from (optional)</Label>
            <Input
              id="removeFrom"
              type="date"
              value={removePastFrom}
              onChange={(e) => setRemovePastFrom(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to keep all past transactions. Only transactions this subscription
              generated are removed — linked ones you imported or entered yourself are kept
              (just unlinked).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={isCancelling}>
              Keep it
            </Button>
            <Button variant="destructive" onClick={confirmCancel} disabled={isCancelling}>
              {isCancelling ? "Cancelling…" : "Cancel recurring expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ResponsiveContainer>
  );
};
